/**
 * *Empresa* — a company administrator's own settings: when its documents finish by themselves (#180),
 * what its drivers may do from the DAC app, and the phones linked to the company (#110, spec §6, issue §E).
 *
 * **The organisation is the signed-in one, never the chosen one.** The phone-link routes take it
 * from the path, and the chosen company (`localStorage['deca.organisation']`) outlives a sign-out.
 * An ORG_ADMIN has no selector to clear it, so a path built from it would 404 every call for a
 * company someone else left chosen in this browser. Only ORG_ADMIN reaches this screen
 * (`managesCompany`); a super admin sets the two boxes and the period in *Editar empresa* instead.
 *
 * **Each box, and the period, saves itself, and sends its own key only.** The server merges
 * settings key by key and the PATCH carries no ETag, so a form sending both boxes from local state
 * would undo a change someone else made to the other box meanwhile. What a box shows is the
 * server's last answer: a refused save leaves it showing what is really in force.
 *
 * **The period waits for the choosing to pause** (`PERIOD_SAVE_DELAY_MS`). An arrow key on a closed
 * select changes it, and fires `change`, at every step: from 2 to 7 days, every period in between
 * would be saved, each a PATCH, a toast and an audit row. So the latest choice is shown at once,
 * busy, and only it is sent; if another save is in flight when the pause ends, it goes when that one
 * ends, rather than being dropped as a box's change is. A choice that comes back to the period in
 * force is not sent: it would change nothing, and still toast and audit a save. Leaving the screen
 * ends the pause: the select showed the choice, so it is sent then, or when the save in flight ends.
 * Its toast shows on the next screen and the query cache takes the answer; a refusal, whose notice
 * has left with the screen, goes to a toast in the same words.
 *
 * Built for a phone (#111): cards rather than a table, one column, buttons that wrap.
 */

import { useEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  unwrap,
  type Organisation,
  type OrganisationSettings,
  type PhoneLink,
  type PhoneLinkCode,
  type PhoneLinkCodeCreated,
} from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage, autocompletePeriodRefusal, undeliverableEmailMessage } from '../app/errors.ts'
import { invalidatePhoneLinks, qk, useOrganisation, usePhoneLinkCodes, usePhoneLinks } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { formatDateTime } from '../deca/format.ts'
import { AdminShell } from './AdminShell.tsx'
import { Button, Card, CopyButton, EmptyState, ErrorState, NoAccessState, Notice } from '../ui/primitives.tsx'
import { Dialog } from '../ui/Dialog.tsx'
import { TextInput } from '../ui/form.tsx'
import { SettingBox } from '../ui/SettingBox.tsx'
import { AutocompleteHoursSelect, DEFAULT_AUTOCOMPLETE_HOURS, type AutocompleteHours } from './AutocompleteHoursSelect.tsx'
import { useToast } from '../ui/Toast.tsx'
import { isEmailAddress } from '../deca/emailAddress.ts'

/**
 * How long the period waits after its last change before it is sent (#180): longer than the steps of
 * an arrow key pressed again, short enough to read as saving at once.
 */
export const PERIOD_SAVE_DELAY_MS = 700

type BoxKey = 'driversMayCreate' | 'driversMayModify'
const BOXES: BoxKey[] = ['driversMayCreate', 'driversMayModify']

/** The change in flight: shown on its control until the server answers, and then the answer is. */
type Saving = { key: BoxKey; value: boolean } | { key: 'autocompleteHours'; value: AutocompleteHours }

export function AdminCompanyScreen() {
  const { t } = useTranslation()
  const client = useQueryClient()
  const toast = useToast()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const orgId = principal?.organisation.id ?? ''
  const allowed = abilities.managesCompany && orgId !== ''

  // Every hook above the refusal below: React requires the same hooks on every render.
  const organisation = useOrganisation(allowed ? orgId : undefined)
  // The codes list polls itself while it holds a pending code, and the phones list polls while it
  // does: a driver typing a code moves it from one list to the other.
  const codes = usePhoneLinkCodes(orgId, allowed)
  const links = usePhoneLinks(orgId, allowed, (codes.data?.data.length ?? 0) > 0)
  useRereadPhonesWhenACodeLeaves(codes.data?.data, orgId)
  // The change in flight (`Saving`), and a ref saying whether there is one, for code that runs
  // between renders.
  const [saving, setSaving] = useState<Saving | null>(null)
  const savingRef = useRef(false)
  // The period chosen and not yet sent (see the top of the file): state to show it, and a ref for the
  // timer and the end of a save, which read it after the render that set it.
  const [pendingPeriod, setPendingPeriod] = useState<AutocompleteHours | null>(null)
  const pendingPeriodRef = useRef<AutocompleteHours | null>(null)
  const periodTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Whether the screen is still there to show a refusal in its notice (see the unmount below).
  const onScreen = useRef(true)
  const [error, setError] = useState<string | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)

  // The notice is at the top, and the boxes and phone cards that report into it are below: on a
  // phone's single column a failure appeared off screen, and the tap seemed to do nothing (#111).
  // `nearest` leaves a notice already in view where it is. `?.` because jsdom has no `scrollIntoView`.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [error])

  // Leaving the screen ends the period's pause (see the top of the file). It runs after the last
  // render, so it calls that render's `sendPendingPeriod`, kept here by an effect. `onScreen` is set
  // again on every mount: StrictMode unmounts the screen once and mounts it again.
  const sendPendingPeriodOfLastRender = useRef<() => void>(() => undefined)
  useEffect(() => {
    sendPendingPeriodOfLastRender.current = sendPendingPeriod
  })
  useEffect(() => {
    onScreen.current = true
    return () => {
      onScreen.current = false
      if (periodTimer.current === null) return
      clearTimeout(periodTimer.current)
      periodTimer.current = null
      sendPendingPeriodOfLastRender.current()
    }
  }, [])

  if (!allowed) {
    return (
      <AdminShell>
        <Card>
          <NoAccessState />
        </Card>
      </AdminShell>
    )
  }

  async function save(change: Saving) {
    // One save at a time, and the controls are never disabled to enforce it: a focused control that
    // is disabled loses its focus to the page body, on every keyboard change. A box changed while
    // another save is in flight is dropped, and snaps back to the server's answer; the period is
    // never sent while one is (`sendPendingPeriod`).
    if (savingRef.current) return
    savingRef.current = true
    setSaving(change)
    setError(null)
    try {
      // One key, never two (see the top of the file).
      const settings: Partial<OrganisationSettings> = { [change.key]: change.value }
      const updated = await unwrap(
        api.PATCH('/api/v1/organisations/{orgId}', {
          params: { path: { orgId } },
          // The generator's artefact, as in `EditOrganisationDialog`: `OrganisationSettings` marks
          // every setting with a `default` required, and an ORG_ADMIN sending `retentionMonths` or
          // `directDownloadDays`, even unchanged, is refused with 403 FIELD_NOT_PERMITTED.
          body: { settings: settings as OrganisationSettings },
        }),
      )
      client.setQueryData(qk.organisation(orgId), updated)
      toast.success(t('admin.company.settingSaved'))
    } catch (caught) {
      const message = autocompletePeriodRefusal(caught, t) ?? apiErrorMessage(caught, t)
      // The notice is the screen's: a refusal that comes after leaving it goes to a toast instead.
      if (onScreen.current) setError(message)
      else toast.error(message)
    } finally {
      savingRef.current = false
      setSaving(null)
      // A period whose pause ran out while this was in flight goes now.
      if (periodTimer.current === null) sendPendingPeriod()
    }
  }

  /** The period chosen: shown at once, and sent once the choosing pauses. */
  function choosePeriod(hours: AutocompleteHours) {
    pendingPeriodRef.current = hours
    setPendingPeriod(hours)
    if (periodTimer.current !== null) clearTimeout(periodTimer.current)
    periodTimer.current = setTimeout(() => {
      periodTimer.current = null
      sendPendingPeriod()
    }, PERIOD_SAVE_DELAY_MS)
  }

  /** Sends the latest period chosen, unless a save is in flight: the end of that one sends it. */
  function sendPendingPeriod() {
    const hours = pendingPeriodRef.current
    if (hours === null || savingRef.current) return
    pendingPeriodRef.current = null
    setPendingPeriod(null)
    // Away and back to the period in force: nothing would change, so nothing is saved. Read from the
    // cache, not this render, which may be older: nothing is sent while a save is in flight, so the
    // answer to any save already sent, the period stepped back to included, is there by now.
    const inForce =
      client.getQueryData<Organisation>(qk.organisation(orgId))?.settings?.autocompleteHours ?? DEFAULT_AUTOCOMPLETE_HOURS
    if (hours === inForce) return
    void save({ key: 'autocompleteHours', value: hours })
  }

  const settings = organisation.data?.settings
  /** What a box shows: the change being saved on it, else the server's last answer. */
  const boxChecked = (key: BoxKey): boolean =>
    saving !== null && saving.key !== 'autocompleteHours' && saving.key === key
      ? saving.value
      : (settings?.[key] ?? false)
  /** What the period shows: the choice not yet sent, else the one in flight, else the server's answer. */
  const period: AutocompleteHours =
    pendingPeriod ??
    (saving?.key === 'autocompleteHours' ? saving.value : (settings?.autocompleteHours ?? DEFAULT_AUTOCOMPLETE_HOURS))
  /** From the choice until the server answers it; not while only a box saves. */
  const periodBusy = pendingPeriod !== null || saving?.key === 'autocompleteHours'
  /** After any act on a phone or a code: typing a code moves it from one list to the other. */
  const onChanged = () => invalidatePhoneLinks(client, orgId)

  return (
    <AdminShell
      heading={t('admin.company.title', { organisation: principal?.organisation.legalName ?? '' })}
      subtitle={t('admin.company.subtitle')}
    >
      {error ? (
        <div ref={errorRef} className="admin-company__error">
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}

      {/* The select only once the period in force is read: before, it would claim 48, a period nobody
          has read. While the read fails the card is not drawn at all, and the one below shows why. */}
      {organisation.error ? null : (
        <Card title={t('admin.company.documentsTitle')} subtitle={t('admin.company.documentsSubtitle')}>
          {settings ? (
            <AutocompleteHoursSelect value={period} busy={periodBusy} onChange={choosePeriod} />
          ) : (
            <p className="small muted">{t('common.loading')}</p>
          )}
        </Card>
      )}

      <Card title={t('admin.company.appTitle')} subtitle={t('admin.company.appSubtitle')}>
        {organisation.error ? (
          <ErrorState error={organisation.error} onRetry={() => void organisation.refetch()} />
        ) : (
          <div className="stack-md">
            {BOXES.map((key) => (
              <SettingBox
                key={key}
                label={t(`admin.company.${key}`)}
                help={t(`admin.company.${key}Help`)}
                checked={boxChecked(key)}
                disabled={!settings}
                busy={saving?.key === key}
                onChange={(checked) => void save({ key, value: checked })}
              />
            ))}
          </div>
        )}
      </Card>

      {organisation.error || !settings ? null : (
        <ReplyToCard orgId={orgId} current={settings.decaEmailReplyTo ?? null} />
      )}

      <LinkedPhonesCard
        orgId={orgId}
        links={links}
        onLink={() => setLinkOpen(true)}
        onChanged={onChanged}
        onError={setError}
      />

      <PendingCodesCard orgId={orgId} codes={codes} onChanged={onChanged} onError={setError} />

      <CreateCodeDialog orgId={orgId} open={linkOpen} onClose={() => setLinkOpen(false)} onCreated={onChanged} />
    </AdminShell>
  )
}

/**
 * When a code leaves the pending list (typed by a driver, revoked, or expired), re-read the phones.
 *
 * Polling both lists is not enough on its own. Each list's timer restarts from its own answer, so
 * the two drift apart, and when the codes poll is the first to see the code gone, the phones poll
 * is switched off (nothing is pending any more) before it fires. The office would see the code
 * vanish and no phone arrive. This makes the codes list's answer re-read the phones itself.
 */
function useRereadPhonesWhenACodeLeaves(pending: PhoneLinkCode[] | undefined, orgId: string) {
  const client = useQueryClient()
  const before = useRef<string[] | null>(null)
  useEffect(() => {
    if (!pending) return
    const now = pending.map((code) => code.id)
    const previous = before.current
    before.current = now
    if (previous?.some((id) => !now.includes(id))) {
      void client.invalidateQueries({ queryKey: qk.phoneLinks(orgId) })
    }
  }, [pending, client, orgId])
}

/** The contract's bound on a phone's name: `label`, minLength 1, maxLength 60. */
const LABEL_MAX = 60

/**
 * The trimmed name, or the message saying why it cannot be sent. The server says the same in a
 * 400, but in Spanish only, or in Fastify's English; this says it in the user's language first.
 */
function checkedLabel(raw: string, t: TFunction): { value: string; error?: undefined } | { error: string } {
  const value = raw.trim()
  if (!value) return { error: t('errors.required') }
  if (value.length > LABEL_MAX) return { error: t('errors.tooLong', { count: LABEL_MAX }) }
  return { value }
}

/** The codes still waiting for a driver to type them, each revocable. */
function PendingCodesCard({
  orgId,
  codes,
  onChanged,
  onError,
}: {
  orgId: string
  codes: ReturnType<typeof usePhoneLinkCodes>
  onChanged: () => void
  onError: (message: string | null) => void
}) {
  const { t, i18n } = useTranslation()
  const toast = useToast()

  async function revoke(code: PhoneLinkCode) {
    if (!window.confirm(t('admin.company.revokeConfirm', { label: code.label }))) return
    onError(null)
    try {
      await unwrap(
        api.DELETE('/api/v1/organisations/{orgId}/phone-link-codes/{codeId}', {
          params: { path: { orgId, codeId: code.id } },
        }),
      )
      toast.success(t('admin.company.revoked'))
    } catch (caught) {
      onError(apiErrorMessage(caught, t))
    } finally {
      // Either way: a 404 here most often means the driver has just typed the code, so the phone
      // list has changed as well as this one.
      onChanged()
    }
  }

  const pending = codes.data?.data ?? []

  return (
    <Card title={t('admin.company.pendingCodes')} subtitle={t('admin.company.linkSubtitle')}>
      {codes.error ? (
        <ErrorState error={codes.error} onRetry={() => void codes.refetch()} />
      ) : codes.isLoading ? null : pending.length === 0 ? (
        <p className="small muted">{t('admin.company.noPendingCodes')}</p>
      ) : (
        <ul className="device-list" aria-label={t('admin.company.pendingCodes')}>
          {pending.map((code) => (
            <li key={code.id} className="device-card">
              <div className="device-card__main">
                <span className="device-card__title">{code.label}</span>
                <span className="small muted">
                  {t('admin.company.expires', { date: formatDateTime(code.expiresAt, i18n.language) })}
                </span>
              </div>
              <div className="device-card__actions">
                <Button icon="ban" onClick={() => void revoke(code)}>
                  {t('admin.company.revoke')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

/** The phones linked to the company: name, platform, device, when linked, when last used. */
function LinkedPhonesCard({
  orgId,
  links,
  onLink,
  onChanged,
  onError,
}: {
  orgId: string
  links: ReturnType<typeof usePhoneLinks>
  onLink: () => void
  onChanged: () => void
  onError: (message: string | null) => void
}) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [renaming, setRenaming] = useState<PhoneLink | null>(null)

  async function unlink(link: PhoneLink) {
    if (!window.confirm(t('admin.company.unlinkConfirm', { label: link.label }))) return
    onError(null)
    try {
      await unwrap(
        api.DELETE('/api/v1/organisations/{orgId}/phone-links/{linkId}', {
          params: { path: { orgId, linkId: link.id } },
        }),
      )
      toast.success(t('admin.company.unlinked'))
    } catch (caught) {
      onError(apiErrorMessage(caught, t))
    } finally {
      // Either way: a 404 means the driver unlinked it from the app first.
      onChanged()
    }
  }

  const phones = links.data?.data ?? []

  return (
    <Card
      title={t('admin.company.phonesTitle')}
      subtitle={t('admin.company.phonesSubtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={onLink}>
          {t('admin.company.link')}
        </Button>
      }
    >
      {links.error ? (
        <ErrorState error={links.error} onRetry={() => void links.refetch()} />
      ) : links.isLoading ? null : phones.length === 0 ? (
        <EmptyState icon="phone" title={t('admin.company.noPhonesTitle')} body={t('admin.company.noPhonesBody')} />
      ) : (
        <ul className="device-list" aria-label={t('admin.company.phonesTitle')}>
          {phones.map((link) => (
            <li key={link.id} className="device-card">
              <div className="device-card__main">
                <span className="device-card__title">{link.label}</span>
                <span className="small">{deviceLine(link, t)}</span>
                <span className="small muted">
                  {t('admin.company.linkedAt', { date: formatDateTime(link.linkedAt, i18n.language) })}
                </span>
                <span className="small muted">
                  {t('admin.company.lastSeen', {
                    date: link.install.lastSeenAt
                      ? formatDateTime(link.install.lastSeenAt, i18n.language)
                      : t('common.never'),
                  })}
                </span>
              </div>
              <div className="device-card__actions">
                <Button icon="edit" onClick={() => setRenaming(link)}>
                  {t('admin.company.rename')}
                </Button>
                <Button variant="danger" icon="ban" onClick={() => void unlink(link)}>
                  {t('admin.company.unlink')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {renaming ? (
        <RenamePhoneDialog
          orgId={orgId}
          link={renaming}
          onClose={() => setRenaming(null)}
          onChanged={onChanged}
        />
      ) : null}
    </Card>
  )
}

/**
 * "Android · Pixel 8 · 0.7.25": the platform, the device's own name as the phone reported it
 * (spec §9: shown as it came, not editable), and the app version, skipping what is missing.
 */
function deviceLine(link: PhoneLink, t: TFunction): string {
  const platform = link.install.platform === 'ios' ? t('admin.company.platformIos') : t('admin.company.platformAndroid')
  return [platform, link.install.deviceLabel, link.install.appVersion].filter(Boolean).join(' · ')
}

/**
 * The new name applies from now on. Documents the phone already created or changed keep the name
 * it had then: it is written into their history, which is a record, not a view of the list.
 */
function RenamePhoneDialog({
  orgId,
  link,
  onClose,
  onChanged,
}: {
  orgId: string
  link: PhoneLink
  onClose: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const [label, setLabel] = useState(link.label)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    const checked = checkedLabel(label, t)
    if (checked.error !== undefined) {
      setError(checked.error)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.PATCH('/api/v1/organisations/{orgId}/phone-links/{linkId}', {
          params: { path: { orgId, linkId: link.id } },
          body: { label: checked.value },
        }),
      )
      toast.success(t('admin.company.renamed'))
      onChanged()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
      // A 404 is the driver having unlinked the phone from the app: the list behind is stale.
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      busy={busy}
      title={t('admin.company.renameTitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={busy} disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <TextInput
        label={t('admin.company.label')}
        help={t('admin.company.labelHelp')}
        required
        maxLength={LABEL_MAX}
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !busy) void submit()
        }}
      />
    </Dialog>
  )
}

/**
 * The code is shown **once**: only its hash is stored, as with an API key's secret
 * (`CreateApiKeyDialog`, which this follows). It stays on screen until *Hecho*, and the lists
 * are re-read while it is still showing, so when the dialog closes the code is already pending
 * behind it and the screen is already watching for the driver.
 */
function CreateCodeDialog({
  orgId,
  open,
  onClose,
  onCreated,
}: {
  orgId: string
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t, i18n } = useTranslation()
  const [label, setLabel] = useState('')
  const [created, setCreated] = useState<PhoneLinkCodeCreated | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reveal = useRef<HTMLDivElement>(null)

  // The field that had focus is replaced by the code in the same render, and focus would fall to
  // the page body: a screen reader would say nothing about a code that is shown once. Move it here.
  useEffect(() => {
    if (created) reveal.current?.focus()
  }, [created])

  function close() {
    setLabel('')
    setCreated(null)
    setError(null)
    onClose()
  }

  async function submit() {
    const checked = checkedLabel(label, t)
    if (checked.error !== undefined) {
      setError(checked.error)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const code = await unwrap(
        api.POST('/api/v1/organisations/{orgId}/phone-link-codes', {
          params: { path: { orgId } },
          body: { label: checked.value },
        }),
      )
      setCreated(code)
      onCreated()
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      busy={busy}
      title={created ? t('admin.company.codeTitle') : t('admin.company.link')}
      onClose={close}
      footer={
        created ? (
          <Button variant="primary" icon="check" onClick={close}>
            {t('admin.company.codeDone')}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" loading={busy} disabled={busy} onClick={() => void submit()}>
              {t('admin.company.createCode')}
            </Button>
          </>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}

      {created ? (
        <div className="stack-md" ref={reveal} tabIndex={-1}>
          <p>{t('admin.company.codeFor', { label: created.label })}</p>
          <div className="code-reveal">
            <code>{created.code}</code>
            <CopyButton value={created.code} />
          </div>
          <p className="small">
            {t('admin.company.codeExpires', { date: formatDateTime(created.expiresAt, i18n.language) })}
          </p>
          <Notice tone="info" title={t('admin.company.codeHowTitle')}>
            {t('admin.company.codeHow')}
          </Notice>
          <p className="small muted">{t('admin.company.codeOnce')}</p>
        </div>
      ) : (
        <TextInput
          label={t('admin.company.label')}
          help={t('admin.company.labelHelp')}
          placeholder={t('admin.company.labelPlaceholder')}
          required
          maxLength={LABEL_MAX}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !busy) void submit()
          }}
        />
      )}
    </Dialog>
  )
}

/**
 * «Correo para respuestas de los envíos de DeCA» (#219): where replies to the company's DeCAs sent
 * by email go. A typed address, so saved by its own button rather than on each change like the boxes
 * above; emptied, it is cleared, and replies go to the user who sent each DeCA, or to the company's
 * administrators for an automatic sending and a phone's.
 */
function ReplyToCard({ orgId, current }: { orgId: string; current: string | null }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const toast = useToast()
  const [value, setValue] = useState(current ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const typed = value.trim()
  const unchanged = typed === (current ?? '')

  async function save() {
    if (typed !== '' && !isEmailAddress(typed)) {
      setError(t('errors.invalidEmail'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const updated = await unwrap(
        api.PATCH('/api/v1/organisations/{orgId}', {
          params: { path: { orgId } },
          // One key, as every save on this screen (see the top of the file).
          body: { settings: { decaEmailReplyTo: typed === '' ? null : typed } as OrganisationSettings },
        }),
      )
      client.setQueryData(qk.organisation(orgId), updated)
      toast.success(t(typed === '' ? 'admin.company.replyToCleared' : 'admin.company.replyToSaved'))
    } catch (caught) {
      // A domain that takes no mail (#141) is named, as on a user's address: the shippers' replies would bounce.
      setError(undeliverableEmailMessage(caught, t) ?? apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('admin.company.replyToTitle')} subtitle={t('admin.company.replyToSubtitle')}>
      <div className="stack-md">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <TextInput
          label={t('admin.company.replyTo')}
          optional
          type="email"
          maxLength={254}
          help={t('admin.company.replyToHelp')}
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        <div className="row">
          <Button variant="primary" loading={busy} disabled={busy || unchanged} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Card>
  )
}
